import {inject, Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {EMPTY, Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {Action, Store} from '@ngrx/store';
import {asArray, isArray, isDefined} from '@aviary/store/common/typing.helpers';
import {ResponseBaseError} from '@aviary/store/common/response-base.types';
import {ApiErrorCode} from '@aviary/store/common/common.types';
import {
  CUSTOM_ERROR_HANDLERS_CREATOR,
  CustomErrorHandler,
  CustomErrorHandlers,
  Error400Handler,
  Error400Handlers,
  ErrorActionMapper,
  FAKE_ERROR_RESPONSE,
  FakeHttpErrorResponse
} from './http-error-interceptor.types';
import * as Sentry from '@sentry/angular';
import {SnackBarActions} from '@aviary/store/snack-bar/snack-bar.actions';
import {RouterActions} from '@aviary/store/router/router.actions';
import {BYPASS_400_ERROR} from '@aviary/store/http-loading/http-loading.types';

@Injectable()
export class HttpErrorInterceptor<RouteNameType extends string = string> implements HttpInterceptor {

  private readonly store: Store = inject(Store);

  public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const fakeError: FakeHttpErrorResponse = request.context.get(FAKE_ERROR_RESPONSE);

    // If we have a fake error, make a new observable for it and stop the interceptor chain here (which prevents an API call).  Otherwise, do the usual.
    const nextObservable: Observable<HttpEvent<unknown>> = fakeError ? throwError(() => fakeError) : next.handle(request);

    return nextObservable.pipe(
      catchError((originalErrorResponse: unknown) => {
        const errorResponse: HttpErrorResponse | undefined = this.getActualOrFakeErrorResponseFrom(originalErrorResponse, fakeError);
        if (!errorResponse) return throwError(() => originalErrorResponse);

        const errorActionHandlers: CustomErrorHandlers | undefined = request.context.get(CUSTOM_ERROR_HANDLERS_CREATOR)?.(errorResponse);
        const errorActionHandler: CustomErrorHandler | undefined = errorActionHandlers?.[errorResponse.status];
        const errorActionMappers: ErrorActionMapper[] | undefined = errorActionHandler?.actionMappers;
        const customActions: Action[] = this.getCustomActionsFrom(errorActionMappers, errorResponse);

        if (customActions.length > 0) {
          customActions.forEach(action => this.store.dispatch(action));

          return errorActionHandler.discardOriginalErrorResponse ? EMPTY : throwError(() => errorResponse);
        }
        Sentry.captureException(errorResponse.message, {
          extra: {
            name: errorResponse.name,
            status: errorResponse.status,
            url: errorResponse.url,
          }
        });

        // There are no custom handlers for this error response, so do the default thing.
        switch (errorResponse.status) {
          case 400: {
            this.handle400ErrorsFor(request, errorResponse);
            break;
          }

          case 401: {
            this.store.dispatch(SnackBarActions.showErrorWithRouterLink({
              beforeLinkText: 'Action failed. Please try to ',
              linkText: 'log in',
              afterLinkText: '.',
              routeName: 'LOGOUT'
            }));
            return EMPTY; // No need to propagate this error.
          }

          case 403: {
            this.store.dispatch(SnackBarActions.showError('Action not allowed with current permissions.'));
            break;
          }

          case 500: {
            // this.store.dispatch(RouterActions.navigateTo('ERROR_500'));
            this.store.dispatch(SnackBarActions.showError('System error. Please contact your system administrator.'));
            return EMPTY; // No need to propagate this error.
          }
        }

        return throwError(() => errorResponse);
      })
    );
  }

  private getActualOrFakeErrorResponseFrom(originalErrorResponse: unknown, fakeError: FakeHttpErrorResponse): HttpErrorResponse | undefined {
    // There's no fake error defined (the normal case).  So just return the original actual error response as-is, but only if it's an HttpErrorResponse.
    if (!fakeError) return originalErrorResponse instanceof HttpErrorResponse ? originalErrorResponse : undefined;

    // If fakeError is a prebuilt fake error response, simply return it as-is.
    if (fakeError instanceof HttpErrorResponse) return fakeError;

    // If we fell through to here, fakeError is a ApiErrorCode.  So we go with a minimal default 400 error and use the string as the error code.
    return new HttpErrorResponse({
      status: 400,
      error: {
        errors: [
          {
            code: fakeError
          }
        ]
      }
    });
  }

  private getCustomActionsFrom(errorActionMappers: ErrorActionMapper[] | undefined, errorResponse: HttpErrorResponse): Action[] {
    if (!errorActionMappers) return [];

    const apiErrors: ResponseBaseError[] | undefined = errorResponse.error?.errors;
    if (!apiErrors) return [];

    const apiErrorCodes: ApiErrorCode[] = apiErrors.map(error => error.code);

    return errorActionMappers
      .reduce<Action[]>(
        (accumulator, {errorCodes, actions}) => [
          ...accumulator,
          ...apiErrorCodes.some(apiErrorCode => asArray(errorCodes).includes(apiErrorCode)) ? [...asArray(actions)] : []
        ],
        []
      );
  }

  private handle400ErrorsFor(request: HttpRequest<unknown>, response: HttpErrorResponse): void {
    if (request.context.get(BYPASS_400_ERROR)) return;

    // We don't usually expect more than one API error for a 400, but we'll support it if it happens.
    const apiErrors: ResponseBaseError[] = (response.error?.errors ?? []);

    const [displayableMessage, redirectRouteName]: [string, RouteNameType | undefined] = this.getHandlerInfoFor(apiErrors);

    this.store.dispatch(SnackBarActions.showError(displayableMessage));

    if (redirectRouteName) this.store.dispatch(RouterActions.navigateTo(redirectRouteName)); // So far, we're assuming routes with no parameters.
  }

  // Returns all displayable messages (falling back to API error codes) concatenated with newlines, and the first redirect route name encountered, if any.
  private getHandlerInfoFor(apiErrors: ResponseBaseError[]): [string, RouteNameType | undefined] {
    if (apiErrors.length === 0) return ['Unknown error.', undefined];

    interface HandlerInfo {
      messages: string[];
      redirects: (RouteNameType | undefined)[];
    }

    const handlerInfo: HandlerInfo =
      apiErrors.reduce<HandlerInfo>(
        (accumulator, apiError) => {
          const handler: Error400Handler<RouteNameType> | undefined = this.ERROR_400_HANDLERS[apiError.code];

          return {
            messages: [...accumulator.messages, this.getMessageFrom(handler, apiError)],
            redirects: [...accumulator.redirects, this.getRedirectFrom(handler)]
          };
        },
        {
          messages: [],
          redirects: []
        }
      );

    return [
      handlerInfo.messages.join('\n'),
      handlerInfo.redirects.filter(isDefined)[0]
    ];
  }

  private getMessageFrom(handler: Error400Handler, apiError: ResponseBaseError): string {
    // We want '' to fall back to the original error code -- so ||, not ??.
    return (isArray(handler) ? handler[0] : handler) || apiError.message || apiError.code;
  }

  private getRedirectFrom(handler: Error400Handler<RouteNameType>): RouteNameType | undefined {
    return (isArray(handler) ? handler[1] : undefined) ?? undefined;
  }

  private readonly ERROR_400_HANDLERS: Error400Handlers<RouteNameType> = {
    ACTION_BLOCKED: '',
    BRANCH_INVALID_ID: '',
    CATALOG_RECORD_INVALID_ID: '',
    CIRCULATION_EXISTING_CHECKOUT: '',
    CIRCULATION_INVALID_SESSION_ID: '',
    CIRCULATION_INVALID_ITEM_ID: 'Unknown item.',
    CIRCULATION_INVALID_STATUS_FOR_ACTION: '',
    CIRCULATION_NO_VALID_ITEM: '',
    CIRCULATION_INVALID_SHELF_ID: '',
    CIRCULATION_INVALID_COLLECTION_ID: '',
    CIRCULATION_NO_ACTIVE_CHECKOUT: '',
    CHECKOUT_INVALID_ID: '',
    CHECKOUT_INVALID_DUE_DATE: '',
    CHECKOUT_INVALID_STATUS: '',
    CHECKOUT_RENEW_BLOCKED_CIRC_RULE: '',
    CHECKOUT_RENEW_BLOCKED_HOLD: '',
    ENTITY_ID_NOT_FOUND: '',
    ENTITY_USED_AS_REFERENCE: '',
    FRAMEWORK_ERROR: '',
    GENERAL_MISSING_PARAM: '',
    GENERAL_INVALID_VALUE: '',
    GENERAL_ABBREVIATION_IN_USE: '',
    GENERAL_BARCODE_IN_USE: '',
    GENERAL_DUPLICATE_EMAIL: '',
    HOLD_INVALID_ID: '',
    HOLD_INVALID_STATUS_FOR_ACTION: '',
    HOLD_INVALID_PAUSE_DATE: '',
    HOLD_ITEM_BIB_MISMATCH: '',
    HOLD_NO_VALID: '',
    HOLDS_PRESENT_ON_ITEM: '',
    MONTHLY_ITEM_REQUEST_LIMIT_EXCEEDED: '',
    ORG_MISMATCH: '',
    PATRON_ALREADY_REGISTERED: '',
    PATRON_CUSTOM_FIELD_REQUIRED: '',
    PATRON_CURRENT_PIN_INCORRECT: '',
    PATRON_GUARDIAN_REQUIRED: '',
    PATRON_HOLDS_AVAILABLE: '',
    PATRON_INVALID_ID: 'Unknown patron.',
    PATRON_INVALID_STATUS_FOR_ACTION: '',
    PATRON_INVALID_STANDING_FOR_ACTION: '',
    PATRON_NOTIFICATION_METHOD_CONFLICT: '',
    PATRON_NOTIFICATION_METHOD_REQUIRED: '',
    PATRON_ONLINE_ACCESS_REQUIRED: '',
    PATRON_PIN_CHANGE_LOCKED: '',
    PATRON_SMS_PHONE_REQUIRED: '',
    PATRON_SMS_TERMS_REQUIRED: '',
    PATRON_VERIFICATION_ALREADY_USED: '',
    PATRON_VERIFICATION_CODE_INCORRECT: '',
    PATRON_VERIFICATION_EMAIL_SEND_FAILED: '',
    PATRON_VERIFICATION_EXPIRED: '',
    PATRON_VERIFICATION_INVALID_STATUS: '',
    PATRON_VERIFICATION_INVALID_TYPE: '',
    PATRON_VERIFICATION_NOT_APPROVED: '',
    PATRON_VERIFICATION_NOT_FOUND: '',
    PATRON_VERIFICATION_WRONG_REGISTRATION_TYPE: '',
    REQUEST_INVALID_STATUS: '',
    USER_INVALID_ID: '',
    USER_NOT_OWNER: ''
  };
}
